import ListPagination from '../../components/common/ListPagination';
import useEventListFetch from './useEventListFetch';
import { normalizeSelectionValue, normalizeSelectionArray } from './eventListSelection';
import useEventListAssignmentUpdates from './useEventListAssignmentUpdates';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import Sidebar from '../../components/admin/Sidebar';
import { useToast } from '../../components/common/ToastSystem';
import DsButton from '../../components/common/DsButton';
import DsModal from '../../components/common/DsModal';
import ListFootCount from '../../components/common/ListFootCount';
import EventListSkeleton from '../../components/common/events/EventListSkeleton';
import { CalendarGridSkeleton } from '../../components/common/ContentSkeletons';
import { SkeletonLine } from '../../components/common/Skeleton';
import '../../css/admin/events/EventList.css';
import '../../css/common/events/EventListCard.css';
import '../../css/common/events/EventListRow.css';
import '../../css/common/Calendar.css';
import { prefectures, regionMapping, regionOptions } from '../../utils/prefectures';
import { EventNavigableCard as EventListCard, EventNavigableRow as EventListRow } from '../../components/admin/events/EventNavigableListItems';
import useEventCardGridLayout from '../../components/common/events/useEventCardGridLayout';
import { normalizeEventPageSize } from '../../components/common/events/eventCardGridMetrics';
import { normalizeGenresPayload } from '../../utils/genreUtils';
import { useAuth } from '../../contexts/AuthContext';
import { resolveAdminPermission } from '../../constants/adminPermissionCatalog';
import usePaginationHistory from '../usePaginationHistory';
import { listGenres } from '../../api/genres';
import {
  getCastingSummaries,
  getEventDetail,
  getEventTaskSummaries,
  getEventTicketSalesSummary,
  listEventCalendarSummary,
} from '../../api/taskEvents';
import { listAdminArtists } from '../../api/artists';
import { listVenues } from '../../api/venues';
import { appendVariant, buildChatAssetUrl } from '../../components/common/chat/composer/chatAssetUtils';
import DsSelect from '../../components/common/DsSelect';
import { formatItemCount } from '../../utils/datetime';
import useEventListPermissions from './useEventListPermissions';
import useEventListPlayguideSuggestions from './useEventListPlayguideSuggestions';
import {
  MAX_VISIBLE_EVENTS_PER_DAY,
  addDays,
  addMonths,
  buildEventsByDate,
  endOfMonth,
  endOfWeek,
  formatYMD,
  formatYearMonthJP,
  getCalendarGridRange,
  getCalendarEventChipClass,
  isSameDay,
  isSameMonth,
  normalizeDateKey,
  startOfMonth,
  startOfWeek,
  weekdayLabels,
} from './eventListCalendarUtils';

// プレースホルダー画像の配列
const isValidUuid = (value) => {
  if (typeof value !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
};

// yyyy-mm-dd
const fmtDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// 簡易Cookie
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
};
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) return match[2];
  return null;
};

// ストレージキー
const STORAGE_KEY = 'admin_event_list_state_v4';
const SCROLL_KEY  = 'admin_event_list_scrollY';
const PAGE_SIZE_KEY = 'admin_event_list_page_size';
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const FETCH_LIMIT_MULTIPLIER = 10;

const EVENT_INFO_ICON_DEFS = [
  { key: 'seating', label: 'シーティング', icon: 'fa-chair', snake: 'stage_seating_count', camel: 'stageSeatingCount' },
  { key: 'timetable', label: 'タイムテーブル', icon: 'fa-clock', snake: 'timetable_count', camel: 'timetableCount' },
  { key: 'materials', label: 'イベント資料', icon: 'fa-folder-open', snake: 'material_count', camel: 'materialCount' },
  { key: 'images', label: 'イベント画像', icon: 'fa-images', snake: 'image_count', camel: 'imageCount' },
];

const eventInfoCount = (event, snake, camel) => {
  const raw = event?.[snake] ?? event?.[camel] ?? 0;
  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
};

// リロード判定
const getNavEntryType = () => {
  try {
    const nav = performance.getEntriesByType?.('navigation');
    if (nav && nav[0] && nav[0].type) return nav[0].type;
  } catch {}
  return 'navigate';
};

// 本日判定
const isToday = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') return false;
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const [ey, em, ed] = yyyyMmDd.split('-').map(n => parseInt(n, 10));
  return y === ey && m === em && d === ed;
};

// Postgresの int[] "{1,2,3}" → 配列
const parsePgIntArray = (s) => {
  const t = String(s || '').trim();
  if (t.startsWith('{') && t.endsWith('}')) {
    return t.slice(1, -1).split(',').map(x => Number(x)).filter(Number.isInteger);
  }
  return null;
};

const normalizeGenreIds = (maybeGenre) => {
  if (Array.isArray(maybeGenre)) {
    return maybeGenre.map((v) => Number(v)).filter((n) => Number.isInteger(n));
  }
  if (typeof maybeGenre === 'string' && maybeGenre.trim().startsWith('{')) {
    return parsePgIntArray(maybeGenre) || [];
  }
  if (typeof maybeGenre === 'string' && maybeGenre.trim().startsWith('[')) {
    try {
      const arr = JSON.parse(maybeGenre);
      if (Array.isArray(arr)) {
        return arr.map((v) => Number(v)).filter((n) => Number.isInteger(n));
      }
    } catch {}
  }
  if (typeof maybeGenre === 'number') return [Number(maybeGenre)];
  if (typeof maybeGenre === 'string' && maybeGenre) return [maybeGenre];
  return [];
};

// 自動期間ラベル（← 文言を「期間選択」に変更）
const AUTO_RANGE_LABELS = {
  auto: '期間選択',
  today: '今日',
  last7: '過去7日',
  last30: '過去30日',
  thisWeek: '今週',
  thisMonth: '今月',
  nextMonth: '来月',
  lastYear: '昨年',
  thisYear: '今年',
  nextYear: '翌年',
};
const getEventTitle = (event) => {
  const raw =
    event?.name ||
    event?.title ||
    event?.event_name ||
    event?.eventTitle ||
    '';
  return typeof raw === 'string' ? raw : '';
};

const getEventDisplayTitle = (event) => {
  const base = getEventTitle(event) || 'イベント名未設定';
  const performance = event?.performance_type;
  return [base, performance].filter(Boolean).join(' ');
};

const getEventId = (event) => {
  if (!event) return undefined;
  return event.event_uuid || event.eventUuid || event.uuid || event.id;
};

const getEventOccurrenceKey = (event, fallback = '') => {
  const eventId = getEventId(event) || 'event';
  const performanceId =
    event?.display_performance_id ??
    event?.performance_id ??
    event?.event_performance_id ??
    event?.display_performance_sort_order ??
    event?.performance_sort_order;
  const occurrenceParts = [
    eventId,
    performanceId,
    event?.calendar_date_key,
    event?.event_date,
    event?.start_time,
    fallback,
  ].filter((part) => part !== undefined && part !== null && part !== '');

  return occurrenceParts.map((part) => String(part)).join(':');
};

export default function useEventListElement() {
  // API元データ
  const [allFetchedEvents, setAllFetchedEvents] = useState([]);
  // 表示用
  const [events, setEvents] = useState([]);
  const [flyerUrls, setFlyerUrls] = useState({});
  const [flyerLandscapeMap, setFlyerLandscapeMap] = useState({});
  const [flyerLoadedFlags, setFlyerLoadedFlags] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [genres, setGenres] = useState([]);
  const [castingSummaryMap, setCastingSummaryMap] = useState({});
  const [ticketSummaryMap, setTicketSummaryMap] = useState({});
  const [taskSummaryMap, setTaskSummaryMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
    try {
      const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
      return normalizeEventPageSize(stored, DEFAULT_PAGE_SIZE);
    } catch {}
    return DEFAULT_PAGE_SIZE;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const navAction = useNavigationType(); // 'PUSH' | 'POP' | 'REPLACE'

  const [viewMode, setViewMode] = useState('card');
  const [isMobileSearchLayout, setIsMobileSearchLayout] = useState(false);
  const [isCompactCardLayout, setIsCompactCardLayout] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const handlePageSizeChange = useCallback((size) => {
    if (size === itemsPerPage) return;
    setItemsPerPage(size);
    setCurrentPage(1);
  }, [itemsPerPage]);
  const {
    cardGridRef,
    cardGridStyle,
    pageSizeOptions,
  } = useEventCardGridLayout({
    basePageSizeOptions: PAGE_SIZE_OPTIONS,
    currentPage,
    isCompactCardLayout,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
    viewMode,
  });

  // 復元フラグ
 const restoringRef = useRef(false);
 const restoredOnceRef = useRef(false);
 const castingSummaryPendingRef = useRef(new Set());
 const ticketSummaryPendingRef = useRef(new Set());
 const taskSummaryPendingRef = useRef(new Set());
 const originalFlyerCacheRef = useRef(new Map());
 const flyerLoadedMapRef = useRef(new Map());
 const flyerSequenceKeyRef = useRef('');
 const queryAppliedRef = useRef(false);
 const calendarCacheRef = useRef(new Map());
 const calendarRequestSeqRef = useRef(0);
 const lastFetchLimitRef = useRef(0);
 const serverMayHaveMoreRef = useRef(true);
 const [initialReady, setInitialReady] = useState(false);

  const { error: toastError } = useToast();
  const { user } = useAuth();
  const currentAdminUserId = useMemo(
    () => String(user?.userId ?? user?.id ?? user?.adminUserId ?? user?.admin_user_id ?? '').trim(),
    [user]
  );
  const adminRole = String(user?.role || 'general').trim().toLowerCase() || 'general';
  const isExternalAccount = user?.is_external_account === true;
  const isSuperAdmin =
    adminRole === 'admin' ||
    (user?.userType === 'admin' &&
      (user?.permissions === 'admin' ||
        user?.permissions?.includes?.('admin') ||
        user?.permissions?.includes?.('*')));
  const adminPermissions = useMemo(
    () =>
      Array.isArray(user?.permissions)
        ? user.permissions
        : user?.permissions
          ? [user.permissions]
          : [],
    [user?.permissions]
  );
  const canRegisterEvents = useMemo(
    () =>
      isSuperAdmin ||
      resolveAdminPermission({
        role: adminRole,
        isExternalAccount,
        permissions: adminPermissions,
        permissionKey: 'NAV_EVENT_REGISTER',
      }),
    [adminPermissions, adminRole, isExternalAccount, isSuperAdmin]
  );
  const {
    canViewTicketSales,
    canEditTicketSales,
    canViewCastingProgress,
    canViewEventTasks,
  } = useEventListPermissions({ adminRole, isExternalAccount, adminPermissions });

  const API_URL = process.env.REACT_APP_API_URL || '';
  const FLYER_IMAGE_PATH = (process.env.REACT_APP_FLYER_IMAGE_PATH || '/api/images/flyers').replace(/\/$/, '');

  const buildFlyerBaseUrl = useCallback(
    (filename) => buildChatAssetUrl({ apiUrl: API_URL, root: FLYER_IMAGE_PATH, file: filename }),
    [API_URL, FLYER_IMAGE_PATH]
  );

  const handleFlyerLoad = useCallback((eventUuid, target) => {
    if (!eventUuid || !target) return;
    const { naturalWidth, naturalHeight } = target;
    if (!naturalWidth || !naturalHeight) return;
    const isLandscape = naturalWidth > naturalHeight;
    setFlyerLandscapeMap((prev) => {
      if (prev[eventUuid] === isLandscape) return prev;
      return { ...prev, [eventUuid]: isLandscape };
    });
  }, []);
  const markFlyerLoaded = useCallback((index, eventUuid) => {
    if (eventUuid) flyerLoadedMapRef.current.set(eventUuid, true);
    setFlyerLoadedFlags((prev) => {
      if (!Array.isArray(prev)) return prev;
      if (index == null || index < 0 || index >= prev.length) return prev;
      if (prev[index]) return prev;
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);
  const flyerRevealIndex = useMemo(() => {
    if (!Array.isArray(flyerLoadedFlags) || flyerLoadedFlags.length === 0) return -1;
    let index = -1;
    for (let i = 0; i < flyerLoadedFlags.length; i += 1) {
      if (!flyerLoadedFlags[i]) break;
      index = i;
    }
    return index;
  }, [flyerLoadedFlags]);

  // ---------- 検索 state ----------
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [autoRange, setAutoRange] = useState('auto'); // ← セレクトの表示保持

  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [filterEventName, setFilterEventName] = useState('');
  const [filterGenres, setFilterGenres] = useState([]); // values
  const [filterRegions, setFilterRegions] = useState([]);
  const [filterPrefectures, setFilterPrefectures] = useState([]);
  const [filterVenues, setFilterVenues] = useState([]);
  const [filterArtists, setFilterArtists] = useState([]);
  const [filterAssignedOnly, setFilterAssignedOnly] = useState(false);
  const [showVenueFilterPopup, setShowVenueFilterPopup] = useState(false);
  const [showArtistFilterPopup, setShowArtistFilterPopup] = useState(false);
  const [showGenreFilterPopup, setShowGenreFilterPopup] = useState(false);
  const [showRegionFilterPopup, setShowRegionFilterPopup] = useState(false);
  const [showPrefectureFilterPopup, setShowPrefectureFilterPopup] = useState(false);
  const [genreFilterSearch, setGenreFilterSearch] = useState('');
  const [prefectureFilterSearch, setPrefectureFilterSearch] = useState('');
  const [artistFilterSearch, setArtistFilterSearch] = useState('');
  const [artistFilterOptions, setArtistFilterOptions] = useState([]);
  const [artistFilterLoading, setArtistFilterLoading] = useState(false);
  const [artistLookup, setArtistLookup] = useState({});
  const [allVenues, setAllVenues] = useState([]);
  const [venueFilterSearch, setVenueFilterSearch] = useState('');
  const [venueFilterPrefecture, setVenueFilterPrefecture] = useState('');
  const venuesById = useMemo(() => {
    const m = new Map();
    (allVenues || []).forEach((v) => {
      if (v?.id != null) m.set(String(v.id), v);
    });
    return m;
  }, [allVenues]);

  useEffect(() => {
    if (!venuesById.size) return;
    setFilterVenues((prev) => {
      const normalized = normalizeSelectionArray(prev);
      const next = normalized.filter((id) => venuesById.has(String(id)));
      return next.length === normalized.length ? prev : next;
    });
  }, [venuesById]);

  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

  // 並び順の復元（ページ初期表示時）
  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && (saved.sortOrder === 'asc' || saved.sortOrder === 'desc')) {
        setSortOrder(saved.sortOrder);
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  const genreMap = useMemo(() => {
    const map = new Map();
    (genres || []).forEach((genre) => {
      if (genre?.value == null) return;
      map.set(String(genre.value), genre);
    });
    return map;
  }, [genres]);

  const regionSelectionOptions = useMemo(
    () => regionOptions.filter((opt) => opt.value),
    []
  );

  const regionLabelMap = useMemo(
    () => new Map(regionSelectionOptions.map((opt) => [opt.value, opt.label])),
    [regionSelectionOptions]
  );

  const artistSelectionOptions = useMemo(() => {
    const byValue = new Map();
    artistFilterOptions.forEach((option) => {
      if (option?.value) byValue.set(String(option.value), option);
    });
    filterArtists.forEach((artistId) => {
      const key = String(artistId);
      if (byValue.has(key)) return;
      const found = artistLookup[key];
      byValue.set(key, {
        value: key,
        label: found?.label || key,
        meta: found?.meta || '',
      });
    });
    return Array.from(byValue.values());
  }, [artistFilterOptions, artistLookup, filterArtists]);

  const prefectureRegionMap = useMemo(() => {
    const map = {};
    Object.entries(regionMapping).forEach(([region, prefs]) => {
      prefs.forEach((pref) => {
        map[pref] = region;
      });
    });
    return map;
  }, []);

  const prefectureOptions = useMemo(
    () =>
      (prefectures || []).map((pref) => {
        const value = pref.value || pref;
        const label = pref.label || pref.value || pref;
        return {
          value,
          label,
          meta: prefectureRegionMap[value] || '',
        };
      }),
    [prefectureRegionMap]
  );

  const genreOptions = useMemo(() => {
    if (!Array.isArray(genres)) return [];
    return genres
      .map((genre) => {
        const rawValue = genre?.value ?? genre?.id;
        if (rawValue === undefined || rawValue === null || rawValue === '') return null;
        return {
          value: normalizeSelectionValue(rawValue),
          label: genre.label || genre.name || normalizeSelectionValue(rawValue),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
  }, [genres]);

  const selectedRegionPrefectures = useMemo(() => {
    const set = new Set();
    filterRegions.forEach((region) => {
      (regionMapping[region] || []).forEach((pref) => set.add(pref));
    });
    return set;
  }, [filterRegions]);

  const toggleGenreSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    if (!normalized) return;
    setFilterGenres((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      return prevNormalized.includes(normalized)
        ? prevNormalized.filter((v) => v !== normalized)
        : [...prevNormalized, normalized];
    });
  }, []);

  const toggleRegionSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    if (!normalized) return;
    setFilterRegions((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      const exists = prevNormalized.includes(normalized);
      const next = exists
        ? prevNormalized.filter((v) => v !== normalized)
        : [...prevNormalized, normalized];
      setFilterPrefectures((prevPref) => {
        const prevPrefNormalized = normalizeSelectionArray(prevPref);
        if (!exists) {
          const set = new Set(prevPrefNormalized);
          (regionMapping[normalized] || []).forEach((pref) => set.add(pref));
          return Array.from(set);
        }
        const removal = regionMapping[normalized] || [];
        return prevPrefNormalized.filter((pref) => !removal.includes(pref));
      });
      return next;
    });
  }, []);

  const syncRegionsWithPrefectures = useCallback((nextPrefectures) => {
    const normalizedPrefectures = normalizeSelectionArray(nextPrefectures);
    setFilterRegions((prevRegions) =>
      normalizeSelectionArray(prevRegions).filter((region) => {
        const prefs = regionMapping[region] || [];
        return prefs.some((pref) => normalizedPrefectures.includes(pref));
      })
    );
  }, []);

  const updatePrefectures = useCallback(
    (updater) => {
      setFilterPrefectures((prev) => {
        const prevNormalized = normalizeSelectionArray(prev);
        const nextRaw = typeof updater === 'function' ? updater(prevNormalized) : updater;
        const nextNormalized = normalizeSelectionArray(nextRaw);
        syncRegionsWithPrefectures(nextNormalized);
        return nextNormalized;
      });
    },
    [syncRegionsWithPrefectures]
  );

  const togglePrefectureSelection = useCallback(
    (value) => {
      const normalized = normalizeSelectionValue(value);
      if (!normalized) return;
      updatePrefectures((prev) =>
        prev.includes(normalized) ? prev.filter((v) => v !== normalized) : [...prev, normalized]
      );
    },
    [updatePrefectures]
  );

  const removeGenreSelection = useCallback(
    (value) => {
      const normalized = normalizeSelectionValue(value);
      setFilterGenres((prev) => normalizeSelectionArray(prev).filter((v) => v !== normalized));
    },
    []
  );

  const removeRegionSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    setFilterRegions((prev) =>
      normalizeSelectionArray(prev).filter((region) => region !== normalized)
    );
    setFilterPrefectures((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      const removal = new Set(regionMapping[normalized] || []);
      return prevNormalized.filter((pref) => !removal.has(pref));
    });
  }, []);

  const removePrefectureSelection = useCallback(
    (value) => {
      const normalized = normalizeSelectionValue(value);
      updatePrefectures((prev) => prev.filter((pref) => pref !== normalized));
    },
    [updatePrefectures]
  );

  const removeVenueSelection = useCallback(
    (venueId) => {
      const normalized = normalizeSelectionValue(venueId);
      setFilterVenues((prev) =>
        normalizeSelectionArray(prev).filter((v) => v !== normalized)
      );
    },
    []
  );

  const closeGenreFilterPopup = useCallback(() => {
    setShowGenreFilterPopup(false);
    setGenreFilterSearch('');
  }, []);

  const closeRegionFilterPopup = useCallback(() => {
    setShowRegionFilterPopup(false);
  }, []);

  const closePrefectureFilterPopup = useCallback(() => {
    setShowPrefectureFilterPopup(false);
    setPrefectureFilterSearch('');
  }, []);

useEffect(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  const query = window.matchMedia('(max-width: 640px)');
  const handleChange = (event) => setIsCompactCardLayout(event.matches);
  setIsCompactCardLayout(query.matches);
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }
  query.addListener(handleChange);
  return () => query.removeListener(handleChange);
}, []);

useEffect(() => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  const query = window.matchMedia('(max-width: 760px)');
  const handleChange = (event) => setIsMobileSearchLayout(event.matches);
  setIsMobileSearchLayout(query.matches);
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }
  query.addListener(handleChange);
  return () => query.removeListener(handleChange);
}, []);

  // 並び替え：日付 →（同日なら）開始時間
  const compareEventByDateAndStart = useCallback((a, b, order = 'asc') => {
    const sortOrderValue = order === 'desc' ? 'desc' : 'asc';
    if (!a.event_date && !b.event_date) return 0;
    if (!a.event_date) return 1;
    if (!b.event_date) return -1;

    const dateA = new Date(a.event_date);
    const dateB = new Date(b.event_date);

    let diff = dateA - dateB;
    if (diff !== 0) return sortOrderValue === 'asc' ? diff : -diff;

    // カレンダーの追加日程はその日程自身の開始時間で並べる（calendar_start_time）。
    // 通常のカード/リスト表示は calendar_start_time を持たないので start_time を使う。
    const rawTa = a.calendar_start_time || a.start_time;
    const rawTb = b.calendar_start_time || b.start_time;
    const ta = rawTa ? String(rawTa).slice(0, 8) : null;
    const tb = rawTb ? String(rawTb).slice(0, 8) : null;
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return ta.localeCompare(tb);
  }, []);

  const calendarWeeks = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(calendarMonth));
    const gridEnd = endOfWeek(endOfMonth(calendarMonth));
    const days = [];
    let day = gridStart;
    while (day <= gridEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      rows.push(days.slice(i, i + 7));
    }
    return rows;
  }, [calendarMonth]);

  const calendarEventsByDate = useMemo(() => {
    const map = buildEventsByDate(calendarEvents);
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => compareEventByDateAndStart(a, b, 'asc'));
    });
    return map;
  }, [calendarEvents, compareEventByDateAndStart]);

  const calendarSelectedEvents = useMemo(() => {
    const key = normalizeDateKey(calendarSelectedDate);
    return key && calendarEventsByDate[key] ? calendarEventsByDate[key] : [];
  }, [calendarSelectedDate, calendarEventsByDate]);
  const playguideSuggestionEventIds = useEventListPlayguideSuggestions({
    enabled: canEditTicketSales,
    events: viewMode === 'calendar' ? calendarSelectedEvents : currentEvents,
  });

  const handleCalendarPrevMonth = () => {
    setCalendarMonth((prev) => {
      const next = addMonths(prev, -1);
      fetchCalendarMonthEvents(next);
      return next;
    });
    setCalendarSelectedDate(null);
  };

  const handleCalendarNextMonth = () => {
    setCalendarMonth((prev) => {
      const next = addMonths(prev, 1);
      fetchCalendarMonthEvents(next);
      return next;
    });
    setCalendarSelectedDate(null);
  };

  // カレンダーの「担当/全体」トグル。ページ共通の filterAssignedOnly を更新し、
  // 表示中の月を新しいスコープで再取得する（list/card 表示とも状態を共有）。
  const handleCalendarScopeChange = (assigned) => {
    if (assigned === filterAssignedOnly) return;
    setFilterAssignedOnly(assigned);
    setCalendarSelectedDate(null);
    fetchCalendarMonthEvents(calendarMonth, { assignedOnly: assigned });
  };

  const handleViewModeChange = (nextMode) => {
    if (nextMode !== 'card' && nextMode !== 'list' && nextMode !== 'calendar') return;
    setViewMode(nextMode);
    if (nextMode === 'calendar') {
      setShowSearchPopup(false);
      setCalendarSelectedDate(null);
      fetchCalendarMonthEvents(calendarMonth);
      return;
    }
    const start = (filterStartDate || '').trim();
    const end = (filterEndDate || '').trim();
    fetchEvents({
      startDate: start || undefined,
      endDate: end || undefined,
      assignedOnly: filterAssignedOnly,
    });
  };

  const handleCalendarDateClick = (day) => {
    setCalendarSelectedDate(day);
  };

  const resolveVenueDisplay = useCallback((event) => {
    const id = event?.venue_id ?? event?.venue?.id ?? null;
    const venueObj = id ? venuesById.get(String(id)) : null;
    const name = venueObj?.name || event?.venue || '';
    const link = id ? `/admin/workspaces/event-operations/venues/${id}` : null;
    return { name, link };
  }, [venuesById]);

  const renderGenreBadgesRow = (genreList, keyPrefix = 'genre', isConciergeEvent = false) => {
    const hasGenres = Array.isArray(genreList) && genreList.length > 0;
    if (!hasGenres && !isConciergeEvent) return null;
    return (
      <div className="event-list-row__badges" aria-label="イベントジャンル">
        {isConciergeEvent && (
          <span
            key={`${keyPrefix}-concierge`}
            className="genre event-list-row__badge"
            style={{ backgroundColor: 'var(--purple-600)', color: '#fff' }}
          >
            コンシェルジュ
          </span>
        )}
        {hasGenres &&
          genreList.map((genre, index) => {
            const key =
              genre?.id != null
                ? `${keyPrefix}-${genre.id}`
                : `${keyPrefix}-${genre?.value || index}`;
            return (
              <span
                key={key}
                className="genre event-list-row__badge"
                style={{
                  backgroundColor: genre?.color || 'var(--indigo-500)',
                  color: genre?.text_color || '#fff',
                }}
              >
                {genre?.label || genre?.name || genre?.value || 'ジャンル'}
              </span>
            );
          })}
      </div>
    );
  };

  const renderEventInfoIcons = (event, keyPrefix = 'event-info') => {
    const icons = EVENT_INFO_ICON_DEFS.filter((item) => eventInfoCount(event, item.snake, item.camel) > 0);
    if (!icons.length) return null;
    return (
      <div className="event-list-info-icons" aria-label="登録情報">
        {icons.map((item) => (
          <span
            key={`${keyPrefix}-${item.key}`}
            className="event-list-info-icon"
            title={item.label}
            aria-label={item.label}
          >
            <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
          </span>
        ))}
      </div>
    );
  };

  const getCastingProgressStats = (summary) => {
    if (!summary) return { percent: 0 };
    const required = Number(summary.required) || 0;
    const decided = Number(summary.decided) || 0;
    const fulfilled = Number(
      summary.fulfilled != null ? summary.fulfilled : Math.min(decided, required)
    );
    const computedPercent =
      summary.percent != null
        ? Number(summary.percent)
        : required > 0
        ? Math.round((Math.min(fulfilled, required) / required) * 100)
        : 0;
    const percent = Math.max(0, Math.min(100, Math.round(computedPercent)));
    return { percent, required, decided, fulfilled };
  };

  const getTicketProgressStats = (summary) => {
    const sold = Number(summary?.sold_total ?? summary?.soldTotal ?? 0) || 0;
    const capacity = Number(summary?.total_capacity ?? summary?.totalCapacity ?? 0) || 0;
    const pctFromSummary = Number(summary?.progress_pct ?? summary?.progressPct);
    const pct =
      capacity > 0
        ? (sold / capacity) * 100
        : Number.isFinite(pctFromSummary)
        ? pctFromSummary
        : 0;
    const percent = Math.max(0, Math.min(100, Math.round(pct || 0)));
    return { percent, sold, capacity };
  };

  const getTaskProgressStats = (summary) => {
    if (!summary) return null;
    const total = Number(summary?.total ?? summary?.totalCount ?? 0) || 0;
    const open = Number(summary?.open ?? summary?.openCount ?? 0) || 0;
    const done = Number(summary?.done ?? summary?.doneCount ?? 0) || 0;
    const computedTotal = total || open + done;
    if (!computedTotal) return null;
    const percentFromSummary = Number(summary?.percent);
    const percent = Number.isFinite(percentFromSummary)
      ? Math.max(0, Math.min(100, Math.round(percentFromSummary)))
      : Math.max(0, Math.min(100, Math.round((done / computedTotal) * 100)));
    return { percent, total: computedTotal, open, done };
  };

  const renderPeriodFilter = (variant = 'inline') => (
    <div className={`period-filter period-filter--${variant}`}>
      <DsSelect
        className="period-control"
        value={autoRange}
        onChange={(e) => setAutoRangeAndSearch(e.target.value)}
        aria-label="期間を選択"
      >
        {Object.entries(AUTO_RANGE_LABELS).map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </DsSelect>
      <div className="period-filter__dates">
        <input
          type="date"
          value={filterStartDate}
          onChange={(e) => setFilterStartDate(e.target.value)}
          className="period-control"
          aria-label="開始日"
        />
        <span>〜</span>
        <input
          type="date"
          value={filterEndDate}
          onChange={(e) => setFilterEndDate(e.target.value)}
          className="period-control"
          aria-label="終了日"
        />
        <DsButton
          type="button"
          variant="secondary"
          size="sm"
          icon
          className="period-reset-action"
          onClick={handlePeriodReset}
          aria-label="期間をリセット"
          title="期間をリセット"
        >
          <i className="fa-solid fa-arrow-rotate-left" aria-hidden="true"></i>
        </DsButton>
      </div>
      <div className="period-filter__actions">
        <DsButton type="button" className="period-action period-action--block" onClick={handlePeriodSearch}>
          期間検索
        </DsButton>
      </div>
    </div>
  );

  const renderAdvancedFilters = (variant = 'inline') => (
    <div className={`event-advanced-fields event-advanced-fields--${variant}`}>
      <div className="search-field">
        <input
          className="search-control"
          placeholder="キーワード"
          aria-label="キーワード"
          type="text"
          value={filterEventName}
          onChange={(e) => setFilterEventName(e.target.value)}
        />
      </div>
      <div className="search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={() => setShowGenreFilterPopup(true)}
        >
          {filterGenres.length ? `ジャンル選択（${filterGenres.length}件）` : 'ジャンル選択'}
        </DsButton>
      </div>
      <div className="search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={() => setShowRegionFilterPopup(true)}
        >
          {filterRegions.length ? `エリア選択（${filterRegions.length}件）` : 'エリア選択'}
        </DsButton>
      </div>
      <div className="search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={() => setShowPrefectureFilterPopup(true)}
        >
          {filterPrefectures.length ? `都道府県選択（${filterPrefectures.length}件）` : '都道府県選択'}
        </DsButton>
      </div>
      <div className="search-field venue-search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={(e) => {
            e.stopPropagation();
            setShowVenueFilterPopup(true);
            fetchAllVenues();
          }}
        >
          会場を選択{filterVenues.length ? `（${filterVenues.length}件）` : ''}
        </DsButton>
      </div>
      <div className="search-field">
        <DsButton
          type="button"
          variant="secondary"
          className="search-control select-filter-action"
          onClick={openArtistFilterPopup}
        >
          {filterArtists.length ? `参加アーティスト（${filterArtists.length}件）` : '参加アーティスト'}
        </DsButton>
      </div>
    </div>
  );

  const renderAssignedOnlyButton = ({ compact = false } = {}) => (
    <DsButton
      type="button"
      variant="secondary"
      size={compact ? 'sm' : 'md'}
      className={`list-header__assigned-filter${filterAssignedOnly ? ' is-active' : ''}`}
      aria-pressed={filterAssignedOnly}
      title="担当イベントのみで絞り込み"
      onClick={() => applyAssignedOnlyFilter(!filterAssignedOnly)}
    >
      <i className="fa-solid fa-user-check" aria-hidden="true"></i>
      <span>担当</span>
    </DsButton>
  );

  const renderFilterPickerModal = ({
    isOpen,
    title,
    options = [],
    selectedValues = [],
    onToggle,
    onClear,
    onClose,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    helperText,
  }) => {
    if (!isOpen) return null;
    const normalizedSelected = normalizeSelectionArray(selectedValues);
    const keyword =
      typeof searchValue === 'string' ? searchValue.trim().toLowerCase() : '';
    const filteredOptions = Array.isArray(options)
      ? options.filter((opt) => {
          if (!keyword) return true;
          const label = (opt.label || '').toLowerCase();
          const value = normalizeSelectionValue(opt.value).toLowerCase();
          const meta = (opt.meta || '').toLowerCase();
          return label.includes(keyword) || value.includes(keyword) || meta.includes(keyword);
        })
      : [];

    return (
      <DsModal
        open={isOpen}
        onClose={onClose}
        title={title}
        size="md"
        className="filter-multiselect-modal"
        footer={(
          <>
            <DsButton type="button" variant="secondary" size="sm" onClick={onClear}>
              すべて解除
            </DsButton>
            <DsButton type="button" size="sm" onClick={onClose}>
              完了
            </DsButton>
          </>
        )}
      >
        {helperText && <p className="filter-multiselect-helper">{helperText}</p>}
        {typeof searchValue === 'string' && onSearchChange && (
          <div className="filter-multiselect-search">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder || 'キーワードで絞り込み'}
            />
          </div>
        )}
        <div className="filter-multiselect-list" role="listbox" aria-multiselectable="true">
          {filteredOptions.map((opt) => {
            const optionValue = normalizeSelectionValue(opt.value);
            if (!optionValue) return null;
            const checked = normalizedSelected.includes(optionValue);
            return (
              <label
                key={optionValue}
                className={`filter-multiselect-option${checked ? ' is-selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(optionValue)}
                />
                <span className="filter-multiselect-option__label">{opt.label}</span>
                {opt.meta && <span className="filter-multiselect-option__meta">{opt.meta}</span>}
              </label>
            );
          })}
          {!filteredOptions.length && (
            <p className="filter-multiselect-empty">該当する項目がありません</p>
          )}
        </div>
      </DsModal>
    );
  };

  // サムネURLまとめ生成
  const flyerErrorNotifiedRef = useRef(false);

  const generateFlyerUrls = useCallback(async (list, sourceEvents = allFetchedEvents) => {
    const urls = {};
    const sourceList = Array.isArray(sourceEvents) ? sourceEvents : allFetchedEvents;
    const eventsById = sourceList.reduce((acc, item) => {
      if (item?.event_uuid) acc[item.event_uuid] = item;
      return acc;
    }, {});

    for (const event of list) {
      const occurrenceKey = getEventOccurrenceKey(event);
      const fallbackFlyer = buildFlyerBaseUrl(event.flyer_front_url);
      const shouldUseOriginalFlyer =
        event.performance_flag === 'additional'
        && event.use_existing_flyers
        && isValidUuid(event.original_event_uuid)
        && event.original_event_uuid !== event.event_uuid;

      if (shouldUseOriginalFlyer) {
        const cacheKey = event.original_event_uuid;
        if (originalFlyerCacheRef.current.has(cacheKey)) {
          const cachedUrl = originalFlyerCacheRef.current.get(cacheKey);
          urls[occurrenceKey] = cachedUrl ?? fallbackFlyer;
          if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = cachedUrl ?? fallbackFlyer;
          continue;
        }

        const cachedOriginal = eventsById[event.original_event_uuid];
        if (cachedOriginal?.flyer_front_url) {
          const inheritedUrl = buildFlyerBaseUrl(cachedOriginal.flyer_front_url);
          originalFlyerCacheRef.current.set(cacheKey, inheritedUrl);
          urls[occurrenceKey] = inheritedUrl;
          if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = inheritedUrl;
          continue;
        }

        try {
          const originalEvent = await getEventDetail(event.original_event_uuid);
          if (!originalEvent) throw new Error('Original event not found');
          const inheritedUrl = buildFlyerBaseUrl(originalEvent.flyer_front_url);
          originalFlyerCacheRef.current.set(cacheKey, inheritedUrl);
          urls[occurrenceKey] = inheritedUrl;
          if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = inheritedUrl;
        } catch (error) {
          console.error('Error fetching original event flyer:', error);
          if (!flyerErrorNotifiedRef.current) {
            flyerErrorNotifiedRef.current = true;
            toastError('一部フライヤー画像の取得に失敗しました');
          }
          originalFlyerCacheRef.current.set(cacheKey, null);
          urls[occurrenceKey] = fallbackFlyer;
          if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = fallbackFlyer;
        }
      } else {
        urls[occurrenceKey] = fallbackFlyer;
        if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = fallbackFlyer;
      }
    }
    return urls;
  }, [allFetchedEvents, buildFlyerBaseUrl, toastError]);

  // ======== ジャンル辞書 ========
  const genresById = useMemo(() => {
    const m = new Map();
    for (const g of genres) m.set(Number(g.id), g);
    return m;
  }, [genres]);

  const genresByValueLower = useMemo(() => {
    const m = new Map();
    for (const g of genres) if (g?.value) m.set(String(g.value).toLowerCase(), g);
    return m;
  }, [genres]);

  const resolvePrimaryGenreObj = useCallback((maybeGenre) => {
    const ids = normalizeGenreIds(maybeGenre);
    if (ids.length > 0 && Number.isInteger(ids[0])) {
      return genresById.get(Number(ids[0])) || null;
    }
    if (typeof maybeGenre === 'string' && maybeGenre && !maybeGenre.trim().startsWith('{') && !maybeGenre.trim().startsWith('[')) {
      return genresByValueLower.get(maybeGenre.toLowerCase()) || null;
    }
    return null;
  }, [genresById, genresByValueLower]);

  const resolveGenreObjsAll = (maybeGenre) => {
    const out = [];
    const seen = new Set();
    const idsOrValues = normalizeGenreIds(maybeGenre);

    if (idsOrValues.length === 1 && typeof idsOrValues[0] === 'string') {
      const g = genresByValueLower.get(String(idsOrValues[0]).toLowerCase());
      if (g) out.push(g);
      return out;
    }

    for (const raw of idsOrValues) {
      const id = Number(raw);
      if (!Number.isInteger(id)) continue;
      const g = genresById.get(id);
      if (g && !seen.has(g.id)) {
        seen.add(g.id);
        out.push(g);
      }
    }
    return out;
  };

  const genreValueForSearch = useCallback((event) => {
    const g = resolvePrimaryGenreObj(event?.genre);
    return (g?.value || '').toLowerCase();
  }, [resolvePrimaryGenreObj]);

  const GenreBadgeRotator = ({ all }) => {
    const [idx, setIdx] = useState(0);
    const [fading, setFading] = useState(false);
    const holdTimerRef = useRef(null);
    const fadeTimerRef = useRef(null);

    const idsKey = useMemo(
      () => (Array.isArray(all) ? all.map((g) => g?.id).join(',') : ''),
      [all]
    );

    useEffect(() => { setIdx(0); }, [idsKey]);

    useEffect(() => {
      if (!all || all.length <= 1) return;

      const clearTimers = () => {
        if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        holdTimerRef.current = null;
        fadeTimerRef.current = null;
      };
      clearTimers();

      const FADE_MS = 300;
      const HOLD_MS = 2800;

      const prefersReduce =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const scheduleNext = () => {
        holdTimerRef.current = setTimeout(() => {
          if (prefersReduce) {
            setIdx((p) => (p + 1) % all.length);
            scheduleNext();
          } else {
            setFading(true);
            fadeTimerRef.current = setTimeout(() => {
              setIdx((p) => (p + 1) % all.length);
              setFading(false);
              scheduleNext();
            }, FADE_MS);
          }
        }, HOLD_MS);
      };

      scheduleNext();
      return () => clearTimers();
    }, [all, idsKey]);

    const g = all?.[idx];
    if (!g) return null;

    return (
      <p
        className={`genre ${fading ? 'is-fading' : ''}`}
        style={{ backgroundColor: g.color || 'var(--contract-detail-action-blue)', color: g.text_color || 'var(--color-inverse)' }}
      >
        {g.label}
      </p>
    );
  };

  const totalCount = events.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const { goToPage } = usePaginationHistory({ currentPage, setCurrentPage, totalPages });
  const renderPageSizeSelector = (variant = 'default') => (
    <label className={`page-size-selector${variant === 'compact' ? ' page-size-selector--compact' : ''}`}>
      <span className="sr-only">1ページの表示件数</span>
      <DsSelect
        className="page-size-selector__control"
        value={itemsPerPage}
        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
        aria-label="1ページの表示件数"
      >
        {pageSizeOptions.map((size) => (
          <option key={size} value={size}>{size}</option>
        ))}
      </DsSelect>
    </label>
  );

  const isLoadingDisplay = viewMode === 'calendar' ? !initialReady : isLoading || !initialReady;
  const shouldShowEmptyState = initialReady && !isLoading && currentEvents.length === 0;
  const today = new Date();
  const renderEventListSkeleton = () => {
    if (viewMode === 'calendar') return <CalendarGridSkeleton />;
    if (viewMode !== 'card' && viewMode !== 'list') return null;
    return (
      <EventListSkeleton
        viewMode={viewMode}
        count={Math.max(1, Number(itemsPerPage) || DEFAULT_PAGE_SIZE)}
        cardGridRef={cardGridRef}
        cardGridStyle={cardGridStyle}
      />
    );
  };

  // 詳細検索ポップアップ
  const openSearchPopup = () => { fetchAllVenues(); setShowSearchPopup(true); };
  const closeSearchPopup = () => setShowSearchPopup(false);

  const loadCastingSummaries = useCallback(
    async (eventIds) => {
      if (!canViewCastingProgress) return;
      const ids = (Array.isArray(eventIds) ? eventIds : [])
        .map((id) => (typeof id === 'string' ? id : null))
        .filter((id) => id && !castingSummaryPendingRef.current.has(id));
      if (!ids.length) return;
      ids.forEach((id) => castingSummaryPendingRef.current.add(id));
      try {
        const summaries = await getCastingSummaries(ids);
        setCastingSummaryMap((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            if (!Object.prototype.hasOwnProperty.call(next, id)) {
              next[id] = { required: 0, decided: 0, percent: 0 };
            }
          });
          Object.entries(summaries).forEach(([id, summary]) => {
            if (typeof id !== 'string' || !id) return;
            next[id] = summary;
          });
          return next;
        });
      } catch (error) {
        console.error('Error fetching casting summaries:', error);
        setCastingSummaryMap((prev) => {
          const fallback = {};
          ids.forEach((id) => {
            if (!Object.prototype.hasOwnProperty.call(prev, id)) {
              fallback[id] = { required: 0, decided: 0, percent: 0 };
            }
          });
          return Object.keys(fallback).length ? { ...prev, ...fallback } : prev;
        });
      } finally {
        ids.forEach((id) => castingSummaryPendingRef.current.delete(id));
      }
    },
    [canViewCastingProgress]
  );

  const loadTicketSummaries = useCallback(
    async (eventIds) => {
      if (!canViewTicketSales) return;
      const ids = (Array.isArray(eventIds) ? eventIds : [])
        .map((id) => (typeof id === 'string' ? id : null))
        .filter((id) => id && !ticketSummaryPendingRef.current.has(id));
      if (!ids.length) return;
      ids.forEach((id) => ticketSummaryPendingRef.current.add(id));
      try {
        const results = await Promise.all(
          ids.map(async (id) => {
            try {
              const summary = await getEventTicketSalesSummary(id);
              return { id, summary };
            } catch {
              return { id, summary: null };
            }
          })
        );
        setTicketSummaryMap((prev) => {
          const next = { ...prev };
          results.forEach(({ id, summary }) => {
            if (!Object.prototype.hasOwnProperty.call(next, id)) {
              next[id] = summary;
            }
          });
          return next;
        });
      } catch (error) {
        console.error('Error fetching ticket summaries:', error);
        setTicketSummaryMap((prev) => {
          const fallback = {};
          ids.forEach((id) => {
            if (!Object.prototype.hasOwnProperty.call(prev, id)) {
              fallback[id] = null;
            }
          });
          return Object.keys(fallback).length ? { ...prev, ...fallback } : prev;
        });
      } finally {
        ids.forEach((id) => ticketSummaryPendingRef.current.delete(id));
      }
    },
    [canViewTicketSales]
  );

  const loadTaskSummaries = useCallback(
    async (eventIds) => {
      if (!canViewEventTasks) return;

      const ids = (Array.isArray(eventIds) ? eventIds : [])
        .map((id) => (typeof id === 'string' ? id : null))
        .filter((id) => id && !taskSummaryPendingRef.current.has(id));
      if (!ids.length) return;

      ids.forEach((id) => taskSummaryPendingRef.current.add(id));
      try {
        const summaries = await getEventTaskSummaries({ eventUuids: ids });
        setTaskSummaryMap((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            if (!Object.prototype.hasOwnProperty.call(next, id)) {
              next[id] = null;
            }
          });
          Object.entries(summaries).forEach(([id, summary]) => {
            if (typeof id !== 'string' || !id) return;
            next[id] = summary;
          });
          return next;
        });
      } catch (error) {
        console.error('Error fetching task summaries:', error);
        setTaskSummaryMap((prev) => {
          const fallback = {};
          ids.forEach((id) => {
            if (!Object.prototype.hasOwnProperty.call(prev, id)) {
              fallback[id] = null;
            }
          });
          return Object.keys(fallback).length ? { ...prev, ...fallback } : prev;
        });
      } finally {
        ids.forEach((id) => taskSummaryPendingRef.current.delete(id));
      }
    },
    [canViewEventTasks]
  );

  const fetchAllVenues = async () => {
    try {
      const data = await listVenues({ page: 1, limit: 10000 });
      setAllVenues(data.venues);
    } catch (error) {
      console.error('Error fetching all venues:', error);
      toastError('会場一覧の取得に失敗しました');
    }
  };

  const toggleVenueSelection = (venue) => {
    const value = normalizeSelectionValue(venue?.id);
    if (!value) return;
    setFilterVenues((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      return prevNormalized.includes(value)
        ? prevNormalized.filter((v) => v !== value)
        : [...prevNormalized, value];
    });
  };

  const mergeArtistLookup = useCallback((artists = []) => {
    if (!Array.isArray(artists) || !artists.length) return;
    setArtistLookup((prev) => {
      let updated = false;
      const next = { ...prev };
      artists.forEach((artist) => {
        const id = normalizeSelectionValue(artist?.artist_id || artist?.artistId || artist?.id);
        if (!id) return;
        const label = artist?.name || artist?.artist_name || artist?.artistName || artist?.email || id;
        if (next[id]?.label !== label) {
          next[id] = {
            label,
            meta: Array.isArray(artist?.parts) ? artist.parts.filter(Boolean).join(' / ') : '',
          };
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, []);

  const fetchArtistFilterOptions = useCallback(async (search = '') => {
    setArtistFilterLoading(true);
    try {
      const data = await listAdminArtists({
        page: 1,
        limit: 100,
        sort: 'name',
        name: String(search || '').trim(),
      });
      const artists = Array.isArray(data?.artists) ? data.artists : [];
      const options = artists
        .map((artist) => {
          const value = normalizeSelectionValue(artist?.artist_id || artist?.artistId || artist?.id);
          if (!value) return null;
          return {
            value,
            label: artist?.name || artist?.artist_name || artist?.artistName || artist?.email || value,
            meta: Array.isArray(artist?.parts) ? artist.parts.filter(Boolean).join(' / ') : '',
          };
        })
        .filter(Boolean);
      mergeArtistLookup(artists);
      setArtistFilterOptions(options);
    } catch (error) {
      console.error('Error fetching artist filter options:', error);
      toastError('アーティスト一覧の取得に失敗しました');
    } finally {
      setArtistFilterLoading(false);
    }
  }, [mergeArtistLookup, toastError]);

  const toggleArtistSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    if (!normalized) return;
    setFilterArtists((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      return prevNormalized.includes(normalized)
        ? prevNormalized.filter((v) => v !== normalized)
        : [...prevNormalized, normalized];
    });
  }, []);

  const removeArtistSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    setFilterArtists((prev) => normalizeSelectionArray(prev).filter((v) => v !== normalized));
  }, []);

  const openArtistFilterPopup = useCallback(() => {
    setShowArtistFilterPopup(true);
    fetchArtistFilterOptions(artistFilterSearch);
  }, [artistFilterSearch, fetchArtistFilterOptions]);

  const closeArtistFilterPopup = useCallback(() => {
    setShowArtistFilterPopup(false);
    setArtistFilterSearch('');
  }, []);

  useEffect(() => {
    if (!showArtistFilterPopup) return undefined;
    const timer = setTimeout(() => {
      fetchArtistFilterOptions(artistFilterSearch);
    }, 250);
    return () => clearTimeout(timer);
  }, [artistFilterSearch, fetchArtistFilterOptions, showArtistFilterPopup]);

  const isEventAssignedToCurrentAdmin = useCallback((event) => {
    if (event?.is_assigned_to_me === true || event?.isAssignedToMe === true) return true;
    if (event?.is_assigned_to_me === false || event?.isAssignedToMe === false) return false;
    if (!currentAdminUserId) return false;
    const assignedAdmins = Array.isArray(event?.assigned_admins)
      ? event.assigned_admins
      : Array.isArray(event?.assignedAdmins)
        ? event.assignedAdmins
        : [];
    return assignedAdmins.some((admin) => {
      const adminId = admin && typeof admin === 'object'
        ? admin.admin_user_id ?? admin.adminUserId ?? admin.id ?? admin.user_id
        : admin;
      return String(adminId ?? '').trim() === currentAdminUserId;
    });
  }, [currentAdminUserId]);

  // ローカル詳細フィルタ（期間はここで絞らない！）
  const filterLocally = useCallback((baseList, orderOverride, options = {}) => {
    let filtered = Array.isArray(baseList) ? [...baseList] : [];
    if (filterEventName.trim()) {
      const keywords = filterEventName.trim().toLowerCase().split(/\s+/);
      filtered = filtered.filter((event) => {
        const venueText = (resolveVenueDisplay(event).name || '').toLowerCase();
        const genreText = (genreValueForSearch(event) || '').toLowerCase();
        const nameText = (event?.name || '').toLowerCase();
        const prefText = (event?.prefecture || '').toLowerCase();
        const overviewText = (event?.event_overview || '').toLowerCase();
        const programText = (event?.program || '').toLowerCase();
        const ticketText = (event?.ticket_info || event?.ticketInfo || '').toLowerCase();
        return keywords.every((kw) =>
          (nameText && nameText.includes(kw)) ||
          (genreText && genreText.includes(kw)) ||
          (venueText && venueText.includes(kw)) ||
          (prefText && prefText.includes(kw)) ||
          (overviewText && overviewText.includes(kw)) ||
          (programText && programText.includes(kw)) ||
          (ticketText && ticketText.includes(kw))
        );
      });
    }
    if (filterGenres.length) {
      filtered = filtered.filter((event) => {
        const g = (genreValueForSearch(event) || '').toLowerCase();
        return filterGenres.some((val) => String(val).toLowerCase() === g);
      });
    }
    if (filterRegions.length) {
      filtered = filtered.filter((event) => selectedRegionPrefectures.has(event.prefecture));
    }
    if (filterPrefectures.length) {
      filtered = filtered.filter((event) => filterPrefectures.includes(event.prefecture));
    }
    if (filterVenues.length > 0) {
      const selected = new Set(normalizeSelectionArray(filterVenues));
      filtered = filtered.filter((event) => {
        const id = event?.venue_id ?? event?.venue?.id ?? null;
        if (id === null || id === undefined || id === '') return false;
        return selected.has(String(id));
      });
    }
    const assignedFilter = typeof options.assignedOnly === 'boolean'
      ? options.assignedOnly
      : filterAssignedOnly;
    if (assignedFilter) {
      filtered = filtered.filter(isEventAssignedToCurrentAdmin);
    }
    const order = orderOverride === 'asc' || orderOverride === 'desc'
      ? orderOverride
      : sortOrder;
    filtered.sort((a, b) => compareEventByDateAndStart(a, b, order));
    return filtered;
  }, [
    filterEventName,
    filterGenres,
    filterRegions,
    filterPrefectures,
    filterVenues,
    filterAssignedOnly,
    compareEventByDateAndStart,
    genreValueForSearch,
    isEventAssignedToCurrentAdmin,
    selectedRegionPrefectures,
    resolveVenueDisplay,
    sortOrder,
  ]);

  const fetchEvents = useEventListFetch({
    canViewCastingProgress, castingSummaryMap, castingSummaryPendingRef, filterAssignedOnly,
    filterEventName, filterGenres, filterPrefectures, filterVenues,
    filterArtists, filterLocally, itemsPerPage, loadCastingSummaries,
    normalizeSelectionArray, lastFetchLimitRef, serverMayHaveMoreRef, restoringRef,
    setIsLoading, setAllFetchedEvents, setEvents, setCurrentPage,
    toastError,
  });

  const handleAssignmentsSaved = useEventListAssignmentUpdates({ currentAdminUserId, filterAssignedOnly,
    setAllFetchedEvents, setEvents, setCalendarEvents, calendarCacheRef });

  const applyAssignedOnlyFilter = useCallback((nextValue) => {
    setFilterAssignedOnly(nextValue);
    setCurrentPage(1);
    setEvents(filterLocally(allFetchedEvents, undefined, { assignedOnly: nextValue }));
  }, [allFetchedEvents, filterLocally]);

  const fetchCalendarMonthEvents = useCallback(async (month, { prefetchAdjacent = true, assignedOnly = filterAssignedOnly } = {}) => {
    const targetMonth = startOfMonth(month || calendarMonth);
    // 「担当/全体」スコープ別にキャッシュを分ける。
    const scopeKey = assignedOnly ? 'assigned' : 'all';
    const cacheKey = `${scopeKey}:${formatYMD(targetMonth)}`;

    const loadMonth = async (target) => {
      const range = getCalendarGridRange(target);
      const params = new URLSearchParams();
      params.append('startDate', range.startDate);
      params.append('endDate', range.endDate);
      params.append('limit', '5000');
      params.append('scope', assignedOnly ? 'assigned' : 'all');
      return await listEventCalendarSummary('admin', Object.fromEntries(params.entries()));
    };

    const prefetchMonth = async (target) => {
      const key = `${scopeKey}:${formatYMD(startOfMonth(target))}`;
      if (calendarCacheRef.current.has(key)) return;
      try {
        const list = await loadMonth(target);
        calendarCacheRef.current.set(key, list);
      } catch (error) {
        console.warn('calendar prefetch failed', error?.message || error);
      }
    };

    const cached = calendarCacheRef.current.get(cacheKey);
    const requestSeq = ++calendarRequestSeqRef.current;
    setCalendarError('');

    if (cached) {
      setCalendarEvents(cached);
      setCalendarLoading(false);
      if (prefetchAdjacent) {
        prefetchMonth(addMonths(targetMonth, -1));
        prefetchMonth(addMonths(targetMonth, 1));
      }
      return;
    }

    setCalendarEvents([]);
    setCalendarLoading(true);
    try {
      const list = await loadMonth(targetMonth);
      if (requestSeq !== calendarRequestSeqRef.current) return;
      calendarCacheRef.current.set(cacheKey, list);
      setCalendarEvents(list);
      if (prefetchAdjacent) {
        prefetchMonth(addMonths(targetMonth, -1));
        prefetchMonth(addMonths(targetMonth, 1));
      }
    } catch (error) {
      if (requestSeq !== calendarRequestSeqRef.current) return;
      console.error('Error fetching calendar events:', error);
      setCalendarError('カレンダーのイベント取得に失敗しました');
    } finally {
      if (requestSeq === calendarRequestSeqRef.current) {
        setCalendarLoading(false);
      }
    }
  }, [calendarMonth, filterAssignedOnly]);

  const conditionChips = useMemo(() => {
    const chips = [];

    if (filterStartDate || filterEndDate) {
      const from = filterStartDate || '未設定';
      const to = filterEndDate || '未設定';
      chips.push({
        key: 'period',
        label: `期間: ${from}〜${to}`,
        onRemove: () => {
          setFilterStartDate('');
          setFilterEndDate('');
          setAutoRange('auto');
          fetchEvents({});
        },
      });
    }

    if (filterAssignedOnly) {
      chips.push({
        key: 'assigned-only',
        label: '担当のみ',
        onRemove: () => {
          applyAssignedOnlyFilter(false);
        },
      });
    }

    if (filterEventName.trim()) {
      chips.push({
        key: 'keyword',
        label: `キーワード: ${filterEventName.trim()}`,
        onRemove: () => setFilterEventName(''),
      });
    }

    filterGenres.forEach((value) => {
      const label = genreMap.get(String(value))?.label || value;
      chips.push({
        key: `genre-${value}`,
        label,
        onRemove: () => removeGenreSelection(value),
      });
    });

    filterRegions.forEach((value) => {
      const label = regionLabelMap.get(value) || value;
      chips.push({
        key: `region-${value}`,
        label,
        onRemove: () => removeRegionSelection(value),
      });
    });

    filterPrefectures.forEach((value) => {
      chips.push({
        key: `pref-${value}`,
        label: value,
        onRemove: () => removePrefectureSelection(value),
      });
    });

    filterVenues.forEach((venueId) => {
      const label = venuesById.get(String(venueId))?.name || venueId;
      chips.push({
        key: `venue-${venueId}`,
        label,
        onRemove: () => removeVenueSelection(venueId),
      });
    });

    filterArtists.forEach((artistId) => {
      const label = artistLookup[String(artistId)]?.label || artistId;
      chips.push({
        key: `artist-${artistId}`,
        label: `参加: ${label}`,
        onRemove: () => removeArtistSelection(artistId),
      });
    });

    return chips;
  }, [
    filterStartDate,
    filterEndDate,
    filterAssignedOnly,
    filterEventName,
    filterGenres,
    filterRegions,
    filterPrefectures,
    filterVenues,
    filterArtists,
    artistLookup,
    genreMap,
    regionLabelMap,
    removeGenreSelection,
    removeRegionSelection,
    removePrefectureSelection,
    removeVenueSelection,
    removeArtistSelection,
    applyAssignedOnlyFilter,
    fetchEvents,
    venuesById,
  ]);

  const hasActiveConditions = conditionChips.length > 0;

  const fetchGenres = async () => {
    try {
      const data = await listGenres();
      setGenres(normalizeGenresPayload(data));
    } catch (error) {
      console.error('Error fetching genres:', error);
      toastError('ジャンルの取得に失敗しました');
    }
  };

  // ===== 初回 復元 or リセット → 取得 =====
  useEffect(() => {
    const entryType = getNavEntryType();
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch {}

    // 表示モードは常に維持（セッション > Cookie）
    const savedView = (saved?.viewMode === 'card' || saved?.viewMode === 'list' || saved?.viewMode === 'calendar')
      ? saved.viewMode
      : getCookie('admin_event_list_view');
    if (savedView === 'card' || savedView === 'list' || savedView === 'calendar') setViewMode(savedView);

    const shouldReset = (navAction === 'PUSH') && entryType !== 'reload';

    restoringRef.current = true;

    let initialStart = '';
    let initialEnd   = '';
    let initialAssignedOnly = false;
    let initialSortOrder = 'asc';
    const dashboardPresetRange = String(location.state?.presetRange || '').trim();
    const dashboardAssignedOnly = location.state?.assignedOnly === true;
    const dashboardRange = (() => {
      const now = new Date();
      if (dashboardPresetRange === 'currentMonth') {
        return {
          key: 'thisMonth',
          start: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)),
          end: fmtDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
        };
      }
      if (dashboardPresetRange === 'nextMonth') {
        return {
          key: 'nextMonth',
          start: fmtDate(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
          end: fmtDate(new Date(now.getFullYear(), now.getMonth() + 2, 0)),
        };
      }
      return null;
    })();
    const urlPage = Number(new URLSearchParams(window.location.search || '').get('p'));
    const hasUrlPage = Number.isFinite(urlPage) && urlPage >= 1;

    if (shouldReset) {
      initialSortOrder = 'asc';
      setSortOrder('asc');
      initialStart = dashboardRange?.start || '';
      initialEnd = dashboardRange?.end || '';
      initialAssignedOnly = dashboardAssignedOnly;
      setFilterStartDate(initialStart);
      setFilterEndDate(initialEnd);
      setAutoRange(dashboardRange?.key || 'auto');
      setFilterEventName('');
      setFilterGenres([]);
      setFilterRegions([]);
      updatePrefectures([]);
      setFilterVenues([]);
      setFilterArtists([]);
      setFilterAssignedOnly(initialAssignedOnly);
      setCurrentPage(1);

      try {
        sessionStorage.setItem(SCROLL_KEY, '0');
        const payload = {
          viewMode: savedView || 'card',
          sortOrder: 'asc',
          currentPage: 1,
          filterStartDate: initialStart,
          filterEndDate: initialEnd,
          autoRange: dashboardRange?.key || 'auto',
          filterEventName: '',
          filterGenres: [],
          filterRegions: [],
          filterPrefectures: [],
          filterVenues: [],
          filterArtists: [],
          filterAssignedOnly: initialAssignedOnly,
          itemsPerPage
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {}
    } else if (saved) {
      if (saved.sortOrder === 'asc' || saved.sortOrder === 'desc') {
        initialSortOrder = saved.sortOrder;
        setSortOrder(saved.sortOrder);
      } else {
        initialSortOrder = 'asc';
        setSortOrder('asc');
      }
      if (!hasUrlPage && typeof saved.currentPage === 'number' && saved.currentPage >= 1) {
        setCurrentPage(saved.currentPage);
      }
      if (typeof saved.filterStartDate === 'string') { setFilterStartDate(saved.filterStartDate); initialStart = saved.filterStartDate; }
      if (typeof saved.filterEndDate === 'string')   { setFilterEndDate(saved.filterEndDate);     initialEnd   = saved.filterEndDate; }
      if (typeof saved.autoRange === 'string')       { setAutoRange(saved.autoRange); }
      if (typeof saved.filterEventName === 'string') setFilterEventName(saved.filterEventName);
      if (Array.isArray(saved.filterGenres)) setFilterGenres(normalizeSelectionArray(saved.filterGenres));
      else if (typeof saved.filterGenre === 'string' && saved.filterGenre) setFilterGenres(normalizeSelectionArray([saved.filterGenre]));
      if (Array.isArray(saved.filterRegions)) setFilterRegions(normalizeSelectionArray(saved.filterRegions));
      else if (typeof saved.filterRegion === 'string' && saved.filterRegion) setFilterRegions(normalizeSelectionArray([saved.filterRegion]));
      if (Array.isArray(saved.filterPrefectures)) updatePrefectures(normalizeSelectionArray(saved.filterPrefectures));
      else if (typeof saved.filterPrefecture === 'string' && saved.filterPrefecture) updatePrefectures(normalizeSelectionArray([saved.filterPrefecture]));
      if (Array.isArray(saved.filterVenues))         setFilterVenues(normalizeSelectionArray(saved.filterVenues));
      if (Array.isArray(saved.filterArtists))        setFilterArtists(normalizeSelectionArray(saved.filterArtists));
      if (saved.artistLookup && typeof saved.artistLookup === 'object') setArtistLookup(saved.artistLookup);
      if (typeof saved.filterAssignedOnly === 'boolean') {
        setFilterAssignedOnly(saved.filterAssignedOnly);
        initialAssignedOnly = saved.filterAssignedOnly;
      } else {
        setFilterAssignedOnly(false);
      }
      const savedPageSize = normalizeEventPageSize(saved.itemsPerPage, null);
      if (savedPageSize) setItemsPerPage(savedPageSize);
    } else {
      initialSortOrder = 'asc';
      setSortOrder('asc');
      setFilterStartDate('');
      setFilterEndDate('');
      setAutoRange('auto');
      setFilterEventName('');
      setFilterGenres([]);
      setFilterRegions([]);
      updatePrefectures([]);
      setFilterVenues([]);
      setFilterArtists([]);
      setFilterAssignedOnly(false);
      setCurrentPage(1);
    }

    (async () => {
      await Promise.all([fetchGenres(), fetchAllVenues()]);

      // 復元した値を引数で取得
      const initialCalendarMode = savedView === 'calendar';
      if (initialCalendarMode) {
        await fetchCalendarMonthEvents(calendarMonth, { assignedOnly: initialAssignedOnly });
      } else {
        await fetchEvents({
          startDate: initialStart || undefined,
          endDate: initialEnd || undefined,
          isInitial: true,
          sortOrderOverride: initialSortOrder,
          assignedOnly: initialAssignedOnly,
        });
      }

      restoringRef.current = false;
      restoredOnceRef.current = true;
      setInitialReady(true);

      // スクロール位置復元：PUSH遷移時は復元しない
      if (navAction !== 'PUSH') {
        try {
          const y = Number(sessionStorage.getItem(SCROLL_KEY) || '0');
          if (!Number.isNaN(y) && y > 0) {
            setTimeout(() => window.scrollTo(0, y), 0);
          }
        } catch {}
      } else {
        sessionStorage.setItem(SCROLL_KEY, '0');
        window.scrollTo(0, 0);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 会場一覧などからのリンク (?venue=◯◯ホール&range=year など) を解釈して
      // 会場一覧などからのリンク (?venue=◯◯ホール&range=year など) を解釈して検索条件を上書き
  useEffect(() => {
    if (!initialReady) return;
    if (queryAppliedRef.current) return;
    if (typeof window === 'undefined') return;

    const search = window.location.search;
    if (!search) return;

    let venueFromQuery = '';
    let rangeFromQuery = '';
    try {
      const sp = new URLSearchParams(search);
      venueFromQuery = (sp.get('venue') || '').trim();
      rangeFromQuery = (sp.get('range') || '').trim().toLowerCase();
    } catch {
      return;
    }

    if (!venueFromQuery && !rangeFromQuery) return;

    queryAppliedRef.current = true;

    let start = '';
    let end = '';
    const today = new Date();
    if (rangeFromQuery === 'year') {
      end = fmtDate(today);
      const startDateObj = new Date(today);
      startDateObj.setFullYear(startDateObj.getFullYear() - 1);
      start = fmtDate(startDateObj);
    } else {
      // 全期間（過去側の履歴を対象）: endDate を今日に設定
      end = fmtDate(today);
      start = '';
    }

    const venueIdFromQuery = /^\d+$/.test(venueFromQuery) ? venueFromQuery : '';
    setFilterVenues(venueIdFromQuery ? [venueIdFromQuery] : []);
    setFilterStartDate(start);
    setFilterEndDate(end);
    setAutoRange('auto');
    setCurrentPage(1);

    fetchEvents({
      startDate: start || undefined,
      endDate: end || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReady]);

  // 現在ページのスライス
  useEffect(() => {
    const newCurrentEvents = events.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    setCurrentEvents(newCurrentEvents);
    flyerErrorNotifiedRef.current = false;
    let cancelled = false;
    const sourceEvents = viewMode === 'calendar' ? calendarSelectedEvents : newCurrentEvents;
    if (sourceEvents.length === 0) {
      setFlyerUrls({});
      return () => { cancelled = true; };
    }
    generateFlyerUrls(sourceEvents, allFetchedEvents)
      .then((urls) => {
        if (!cancelled) setFlyerUrls(urls);
      });
    return () => { cancelled = true; };
  }, [allFetchedEvents, calendarSelectedEvents, currentPage, events, generateFlyerUrls, itemsPerPage, viewMode]);

  useEffect(() => {
    if (!canViewTicketSales) return;
    if (viewMode !== 'card' && viewMode !== 'list' && viewMode !== 'calendar') return;
    const sourceEvents = viewMode === 'calendar' ? calendarSelectedEvents : currentEvents;
    const ids = (sourceEvents || [])
      .map((event) => event?.event_uuid)
      .filter((id) => typeof id === 'string' && id.length > 0)
      .filter(
        (id) =>
          !Object.prototype.hasOwnProperty.call(ticketSummaryMap, id) &&
          !ticketSummaryPendingRef.current.has(id)
      );
    if (ids.length) {
      loadTicketSummaries(ids);
    }
  }, [
    canViewTicketSales,
    viewMode,
    currentEvents,
    calendarSelectedEvents,
    ticketSummaryMap,
    loadTicketSummaries,
  ]);

  useEffect(() => {
    if (!canViewEventTasks) return;
    if (viewMode !== 'card' && viewMode !== 'list' && viewMode !== 'calendar') return;
    const sourceEvents = viewMode === 'calendar' ? calendarSelectedEvents : currentEvents;
    const ids = (sourceEvents || [])
      .map((event) => event?.event_uuid)
      .filter((id) => typeof id === 'string' && id.length > 0)
      .filter(
        (id) =>
          !Object.prototype.hasOwnProperty.call(taskSummaryMap, id) &&
          !taskSummaryPendingRef.current.has(id)
      );
    if (ids.length) {
      loadTaskSummaries(ids);
    }
  }, [
    canViewEventTasks,
    viewMode,
    currentEvents,
    calendarSelectedEvents,
    taskSummaryMap,
    loadTaskSummaries,
  ]);

  // ページ超過時の補正
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(events.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [events.length, itemsPerPage, currentPage]);

  // 詳細フィルタ条件変更 → サーバー再検索
  useEffect(() => {
    if (!restoredOnceRef.current) return;
    if (viewMode === 'calendar') return;
    const timer = setTimeout(() => {
      const start = (filterStartDate || '').trim();
      const end = (filterEndDate || '').trim();
      fetchEvents({
        startDate: start || undefined,
        endDate: end || undefined,
        assignedOnly: filterAssignedOnly,
      });
    }, filterEventName.trim() ? 250 : 0);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEventName, filterGenres, filterPrefectures, filterVenues, filterArtists]);

  // 並び順変更は取得済み結果にのみ適用
  useEffect(() => {
    if (!restoredOnceRef.current) return;
    const filtered = filterLocally(allFetchedEvents);
    setEvents(filtered);
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  // 並び順切替
  const toggleSortOrder = () => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));

  // 期間選択（完全制御＋即検索）
  const setAutoRangeAndSearch = (val) => {
    setAutoRange(val);
    const now = new Date();
    const startOfWeek = () => { const d = new Date(now); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); return d; };
    const endOfWeek = () => { const d = startOfWeek(); d.setDate(d.getDate() + 6); return d; };
    const startOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth(), 1);
    const endOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
    const startOfNextMonth = () => new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const endOfNextMonth = () => new Date(now.getFullYear(), now.getMonth() + 2, 0);
    const startOfLastYear = () => new Date(now.getFullYear() - 1, 0, 1);
    const endOfLastYear = () => new Date(now.getFullYear() - 1, 11, 31);
    const startOfYear = () => new Date(now.getFullYear(), 0, 1);
    const endOfYear = () => new Date(now.getFullYear(), 11, 31);
    const startOfNextYear = () => new Date(now.getFullYear() + 1, 0, 1);
    const endOfNextYear = () => new Date(now.getFullYear() + 1, 11, 31);

    let s = '', e = '';
    switch (val) {
      case 'today':     s = fmtDate(now); e = fmtDate(now); break;
      case 'last7':     { const d = new Date(now); d.setDate(d.getDate()-6); s = fmtDate(d); e = fmtDate(now); } break;
      case 'last30':    { const d = new Date(now); d.setDate(d.getDate()-29); s = fmtDate(d); e = fmtDate(now); } break;
      case 'thisWeek':  s = fmtDate(startOfWeek()); e = fmtDate(endOfWeek()); break;
      case 'thisMonth': s = fmtDate(startOfMonth(now)); e = fmtDate(endOfMonth(now)); break;
      case 'nextMonth': s = fmtDate(startOfNextMonth()); e = fmtDate(endOfNextMonth()); break;
      case 'lastYear':  s = fmtDate(startOfLastYear()); e = fmtDate(endOfLastYear()); break;
      case 'thisYear':  s = fmtDate(startOfYear()); e = fmtDate(endOfYear()); break;
      case 'nextYear':  s = fmtDate(startOfNextYear()); e = fmtDate(endOfNextYear()); break;
      default:
        // auto は日付クリアしてデフォルト（今日以降）
        setFilterStartDate('');
        setFilterEndDate('');
        fetchEvents({});
        return;
    }
    setFilterStartDate(s);
    setFilterEndDate(e);
    // 設定した s/e を明示引数で検索
    fetchEvents({ startDate: s, endDate: e });
  };

  // 期間検索ボタン（← クリック時にセレクト表示を初期値へ戻す）
  const handlePeriodSearch = () => {
    // セレクトの表示だけ初期化（検索挙動はそのまま）
    setAutoRange('auto');

    const s = (filterStartDate || '').trim();
    const e = (filterEndDate || '').trim();
    if (!s && !e) {
      // 日付未指定 → デフォルト（今日以降）
      fetchEvents({});
      return;
    }
    fetchEvents({ startDate: s || undefined, endDate: e || undefined });
  };

  // 期間リセット（今日以降）
  const handlePeriodReset = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setAutoRange('auto');
    fetchEvents({});
  };

  const handleResetFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setAutoRange('auto');
    setFilterEventName('');
    setFilterGenres([]);
    setFilterRegions([]);
    updatePrefectures([]);
    setFilterVenues([]);
    setFilterArtists([]);
    setFilterAssignedOnly(false);
    closeGenreFilterPopup();
    closeRegionFilterPopup();
    closePrefectureFilterPopup();
    setShowVenueFilterPopup(false);
    closeArtistFilterPopup();
    setCurrentPage(1);
    fetchEvents({ assignedOnly: false });
    if (isMobileSearchLayout) {
      setShowSearchPopup(false);
    }
  };

  const renderConditionChipBar = () => {
    if (!hasActiveConditions) return null;
    return (
      <div className="condition-chip-bar" aria-live="polite">
        <div className="condition-chip-list" role="list">
          {conditionChips.map((chip) => (
            <span key={chip.key} className="condition-chip" role="listitem">
              <span className="condition-chip__label">{chip.label}</span>
              <button
                type="button"
                className="condition-chip__remove"
                aria-label={`${chip.label} を解除`}
                onClick={chip.onRemove}
              >
                ×
              </button>
            </span>
          ))}

          <button
            type="button"
            className="condition-chip condition-chip-clear"
            onClick={handleResetFilters}
          >
            すべてクリア
          </button>
        </div>
      </div>
    );
  };

  // 詳細ページ
  const goDetail = (uuid) => navigate(`/admin/workspaces/event-operations/events/${uuid}`);
  const resolveThumbSrc = (event) => {
    // カード/リスト/カレンダー（日付選択時のイベント行）の全 viewMode で 600px の card プロキシに統一
    // （ユーザー決定：小枠でもカード同等のくっきり表示を優先）。描画は共通の EventFlyerMedia。
    const variant = 'card';
    const immediateBase = buildFlyerBaseUrl(event.flyer_front_url);
    const occurrenceKey = getEventOccurrenceKey(event);
    const resolvedBase = flyerUrls[occurrenceKey] || flyerUrls[event.event_uuid];
    const chosen = resolvedBase || immediateBase;
    return appendVariant(chosen, variant);
  };

  useEffect(() => {
    if (viewMode === 'calendar') {
      flyerSequenceKeyRef.current = '';
      flyerLoadedMapRef.current.clear();
      setFlyerLoadedFlags([]);
      return;
    }
    const sequenceKey = `${viewMode}:${(currentEvents || [])
      .map((event, index) => getEventOccurrenceKey(event, index))
      .join('|')}`;
    if (flyerSequenceKeyRef.current !== sequenceKey) {
      flyerSequenceKeyRef.current = sequenceKey;
      flyerLoadedMapRef.current.clear();
    }
    const count = (currentEvents || []).length;
    if (!count) {
      setFlyerLoadedFlags([]);
      return;
    }
    setFlyerLoadedFlags(Array.from({ length: count }, () => false));
    const timers = Array.from({ length: count }, (_, index) =>
      setTimeout(() => {
        setFlyerLoadedFlags((prev) => {
          if (!Array.isArray(prev) || prev.length !== count || prev[index]) return prev;
          const next = [...prev];
          next[index] = true;
          return next;
        });
      }, Math.min(index * 36, 540))
    );
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [currentEvents, viewMode]);

  const renderEventRow = (event, index) => {
    const occurrenceKey = getEventOccurrenceKey(event, index);
    const isTodayEvent = isToday(event.event_date);
    const isCancelledEvent = !!event?.canceled_at;
    const imgSrc = resolveThumbSrc(event);
    const isLandscapeFlyer = !!flyerLandscapeMap[occurrenceKey];
    const genreObjsForRow = resolveGenreObjsAll(event.genre);
    const isConciergeEvent = Boolean(event?.is_concierge_event || event?.concierge_application_id);
    const venueDisplay = resolveVenueDisplay(event);
    const venueForRow =
      venueDisplay.name || venueDisplay.link
        ? { ...venueDisplay, name: venueDisplay.name || '会場未設定' }
        : null;
    const taskStats = canViewEventTasks
      ? getTaskProgressStats(taskSummaryMap[event.event_uuid])
      : null;
    const castingStats = canViewCastingProgress
      ? getCastingProgressStats(castingSummaryMap[event.event_uuid])
      : { percent: 0 };
    const ticketStats = canViewTicketSales
      ? getTicketProgressStats(ticketSummaryMap[event.event_uuid])
      : { percent: 0 };
    const progressItems = [
      ...(canViewEventTasks
        ? [
            {
              key: 'task',
              label: 'Tasks',
              percent: taskStats?.percent ?? 0,
              tone: 'task',
            },
          ]
        : []),
      ...(canViewCastingProgress
        ? [
            {
              key: 'casting',
              label: 'Casting',
              percent: castingStats.percent,
              tone: 'casting',
            },
          ]
        : []),
      ...(canViewTicketSales
        ? [
            {
              key: 'ticket',
              label: 'Tickets',
              percent: ticketStats.percent,
              tone: 'ticket',
            },
          ]
        : []),
    ];
    const allowFlyerReveal = viewMode !== 'calendar';
    const flyerReveal = allowFlyerReveal ? index <= flyerRevealIndex : undefined;
    const flyerDelayMs = allowFlyerReveal ? Math.min(index * 40, 600) : undefined;
    const flyerDisplayTimeoutMs = allowFlyerReveal && index === flyerRevealIndex + 1 ? 1000 : 0;
    const flyerLoading = index < 12 ? 'eager' : 'lazy';
    const flyerFetchPriority = index < 4 ? 'high' : 'auto';
    const handleFlyerReady = allowFlyerReveal
      ? () => markFlyerLoaded(index, occurrenceKey)
      : null;
    const handleRowFlyerLoad = (img) => {
      handleFlyerLoad(occurrenceKey, img);
      if (handleFlyerReady) handleFlyerReady();
    };

    return (
      <EventListRow
        key={occurrenceKey}
        onAssignmentsSaved={handleAssignmentsSaved}
        event={event}
        imgSrc={imgSrc}
        imgAlt={event.name ? `${event.name} のフライヤー` : 'フライヤー'}
        isLandscape={isLandscapeFlyer}
        onFlyerLoad={handleRowFlyerLoad}
        onFlyerError={handleFlyerReady || undefined}
        onFlyerDisplayTimeout={handleFlyerReady || undefined}
        flyerReveal={allowFlyerReveal ? flyerReveal : undefined}
        flyerDelayMs={allowFlyerReveal ? flyerDelayMs : undefined}
        flyerDisplayTimeoutMs={flyerDisplayTimeoutMs}
        flyerLoading={flyerLoading}
        flyerFetchPriority={flyerFetchPriority}
        onClick={() => goDetail(event.event_uuid)}
        isToday={isTodayEvent}
        todayLabel="本日"
        isCancelled={isCancelledEvent}
        cancelLabel="中止"
        venueDisplay={venueForRow}
        badges={renderGenreBadgesRow(
          genreObjsForRow,
          `row-genre-${event.event_uuid}`,
          isConciergeEvent,
        )}
        infoIcons={renderEventInfoIcons(event, `row-info-${event.event_uuid}`)}
        progressItems={progressItems}
        noticeLabel={playguideSuggestionEventIds.has(event.event_uuid) ? '券売連動候補あり' : ''}
      />
    );
  };

  // 表示状態の保存（セッション）
  useEffect(() => {
    if (!restoredOnceRef.current) return;
    try {
      const payload = {
        viewMode,
        sortOrder,
        currentPage,
        filterStartDate,
        filterEndDate,
        autoRange,
        filterEventName,
        filterGenres,
        filterRegions,
        filterPrefectures,
        filterVenues,
        filterArtists,
        artistLookup,
        filterAssignedOnly,
        itemsPerPage
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }, [
    viewMode, sortOrder, currentPage, itemsPerPage,
    filterStartDate, filterEndDate, autoRange,
    filterEventName, filterGenres, filterRegions, filterPrefectures, filterVenues, filterArtists, artistLookup, filterAssignedOnly
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(itemsPerPage));
    } catch {}
  }, [itemsPerPage]);

  // 表示モードCookie
  useEffect(() => {
    setCookie('admin_event_list_view', viewMode, 365);
  }, [viewMode]);

  // スクロール位置保存
  useEffect(() => {
    const onScroll = () => {
      try {
        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY || 0));
      } catch {}
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!restoredOnceRef.current) return;
    if (viewMode === 'calendar') return;
    const start = (filterStartDate || '').trim();
    const end = (filterEndDate || '').trim();
    const hasServerSearchFilters =
      Boolean(filterEventName.trim()) ||
      filterGenres.length > 0 ||
      filterPrefectures.length > 0 ||
      filterVenues.length > 0 ||
      filterArtists.length > 0;
    const desiredFetchLimit = hasServerSearchFilters
      ? 5000
      : Math.min(itemsPerPage * FETCH_LIMIT_MULTIPLIER, 1000);
    const requiredCount = currentPage * itemsPerPage;
    const needsVisibleItems = events.length < requiredCount && serverMayHaveMoreRef.current;
    const needsLargerWindow = desiredFetchLimit > lastFetchLimitRef.current && serverMayHaveMoreRef.current;
    if (!needsVisibleItems && !needsLargerWindow) return;

    fetchEvents({
      startDate: start || undefined,
      endDate: end || undefined,
      fetchLimitOverride: Math.max(desiredFetchLimit, requiredCount + itemsPerPage * 2),
      preservePage: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage, currentPage, events.length]);

  return (
    <div className="admin-dashboard app-layout">
      <Sidebar />
      <div className="event-list-container app-main app-main--force">
        <div className="list-header">
          <div className="list-header__bar">
            {!isMobileSearchLayout && (
              <div className="list-header__title">
                <h2 className="list-header__heading">イベント一覧</h2>
                {viewMode !== 'calendar' && initialReady && (
                  <span className="list-header__count">{formatItemCount(totalCount)}</span>
                )}
              </div>
            )}
            <div className="list-header__controls" role="region" aria-label="リスト操作">
              {viewMode !== 'calendar' && renderAssignedOnlyButton({ compact: isMobileSearchLayout })}
              {viewMode !== 'calendar' && (
                <DsButton
                  type="button" aria-label="検索"
                  className={`list-header__search${hasActiveConditions ? ' is-active' : ''}`}
                  onClick={() => openSearchPopup()}
                  aria-haspopup="dialog"
                  aria-expanded={showSearchPopup}
                >
                  <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                  <span>検索</span>
                  {hasActiveConditions ? (
                    <span className="list-header__search-badge">{conditionChips.length}</span>
                  ) : null}
                </DsButton>
              )}
              {viewMode !== 'calendar' && (
                <DsButton type="button" variant="secondary" size="sm" className="sort-toggle-action" onClick={toggleSortOrder}>
                  並び順: {sortOrder === 'asc' ? '昇順' : '降順'}
                </DsButton>
              )}
              {viewMode !== 'calendar' && renderPageSizeSelector()}
              <div className="segmented view-toggle-btns" aria-label="表示切り替え">
                <DsButton
                  type="button"
                  variant="secondary"
                  icon
                  className={`view-action ${viewMode === 'card' ? 'active' : ''}`}
                  onClick={() => handleViewModeChange('card')}
                  title="カード表示"
                  aria-label="カード表示"
                  aria-pressed={viewMode === 'card'}
                >
                  <i className="fa-solid fa-image" aria-hidden="true"></i>
                </DsButton>
                <DsButton
                  type="button"
                  variant="secondary"
                  icon
                  className={`view-action ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => handleViewModeChange('list')}
                  title="リスト表示"
                  aria-label="リスト表示"
                  aria-pressed={viewMode === 'list'}
                >
                  <i className="fa-solid fa-list" aria-hidden="true"></i>
                </DsButton>
                <DsButton
                  type="button"
                  variant="secondary"
                  icon
                  className={`view-action ${viewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => handleViewModeChange('calendar')}
                  title="カレンダー表示"
                  aria-label="カレンダー表示"
                  aria-pressed={viewMode === 'calendar'}
                >
                  <i className="fa-solid fa-calendar-days" aria-hidden="true"></i>
                </DsButton>
              </div>
              {viewMode !== 'calendar' && (
                <span className="list-header__wrap" aria-hidden="true" />
              )}
              {!isMobileSearchLayout && canRegisterEvents && (
                <DsButton
                  type="button"
                  className="list-header__action list-header__action--primary"
                  onClick={() => navigate('/admin/workspaces/event-operations/events/register')}
                >
                  <i className="fa-solid fa-plus" aria-hidden="true"></i>
                  <span>新規登録</span>
                </DsButton>
              )}
            </div>
          </div>
          {viewMode !== 'calendar' && renderConditionChipBar()}
        </div>

        {isMobileSearchLayout && canRegisterEvents && (
          <DsButton
            type="button"
            icon
            className="event-list-register-fab"
            onClick={() => navigate('/admin/workspaces/event-operations/events/register')}
            aria-label="イベントを新規登録"
          >
            <i className="fa-solid fa-plus" aria-hidden="true"></i>
          </DsButton>
        )}

        {/* ===== 詳細検索モーダル（親） ===== */}
        <DsModal
          open={showSearchPopup && viewMode !== 'calendar'}
          onClose={closeSearchPopup}
          title="詳細絞り込み"
          size="sm"
          className="event-list-search-modal"
          footer={(
            <DsButton type="button" variant="secondary" onClick={closeSearchPopup}>
              閉じる
            </DsButton>
          )}
        >
          {renderPeriodFilter('modal')}
          {renderAdvancedFilters('modal')}
        </DsModal>

        {renderFilterPickerModal({
          isOpen: showGenreFilterPopup,
          title: 'ジャンルを選択（複数可）',
          options: genreOptions,
          selectedValues: filterGenres,
          onToggle: toggleGenreSelection,
          onClear: () => setFilterGenres([]),
          onClose: closeGenreFilterPopup,
          searchValue: genreFilterSearch,
          onSearchChange: setGenreFilterSearch,
          searchPlaceholder: 'ジャンル名で絞り込み',
        })}

        {renderFilterPickerModal({
          isOpen: showRegionFilterPopup,
          title: 'エリアを選択（複数可）',
          options: regionSelectionOptions,
          selectedValues: filterRegions,
          onToggle: toggleRegionSelection,
          onClear: () => {
            setFilterRegions([]);
            updatePrefectures([]);
          },
          onClose: closeRegionFilterPopup,
          helperText: 'エリアを選択すると該当する都道府県がまとめて選択されます。',
        })}

        {renderFilterPickerModal({
          isOpen: showPrefectureFilterPopup,
          title: '都道府県を選択（複数可）',
          options: prefectureOptions,
          selectedValues: filterPrefectures,
          onToggle: togglePrefectureSelection,
          onClear: () => updatePrefectures([]),
          onClose: closePrefectureFilterPopup,
          searchValue: prefectureFilterSearch,
          onSearchChange: setPrefectureFilterSearch,
          searchPlaceholder: '都道府県名で検索',
        })}

        {renderFilterPickerModal({
          isOpen: showArtistFilterPopup,
          title: '参加アーティストを選択（複数可）',
          options: artistSelectionOptions,
          selectedValues: filterArtists,
          onToggle: toggleArtistSelection,
          onClear: () => setFilterArtists([]),
          onClose: closeArtistFilterPopup,
          searchValue: artistFilterSearch,
          onSearchChange: setArtistFilterSearch,
          searchPlaceholder: 'アーティスト名で検索',
          helperText: artistFilterLoading ? '取得中...' : '選択した全員が参加しているイベントを表示します。',
        })}

        {/* ===== 会場フィルターモーダル（子） ===== */}
        <DsModal
          open={showVenueFilterPopup}
          onClose={() => setShowVenueFilterPopup(false)}
          title="会場を選択（複数可）"
          size="lg"
          className="venue-filter-modal"
          footer={(
            <>
              <DsButton type="button" variant="secondary" size="sm" onClick={() => setFilterVenues([])}>
                すべて解除
              </DsButton>
              <DsButton type="button" size="sm" onClick={() => setShowVenueFilterPopup(false)}>
                適用して閉じる
              </DsButton>
            </>
          )}
        >
          <div className="venue-filter-controls">
            <input type="text" placeholder="会場名検索" value={venueFilterSearch} onChange={(e) => setVenueFilterSearch(e.target.value)} />
            <DsSelect value={venueFilterPrefecture} onChange={(e) => setVenueFilterPrefecture(e.target.value)} className="prefecturs-select">
              <option value="">都道府県選択</option>
              {prefectures.map(pref => (<option key={pref.value} value={pref.value}>{pref.label}</option>))}
            </DsSelect>
            <DsButton
              type="button"
              variant="secondary"
              size="sm"
              className="venue-filter-reset-action"
              onClick={() => { setVenueFilterSearch(''); setVenueFilterPrefecture(''); }}
            >
              リセット
            </DsButton>
          </div>
          <div className="venue-list">
            {allVenues
              .filter(venue =>
                venue.name.toLowerCase().includes(venueFilterSearch.toLowerCase()) &&
                (venueFilterPrefecture ? venue.prefecture === venueFilterPrefecture : true)
              )
              .map(venue => {
                const checked = normalizeSelectionArray(filterVenues).includes(String(venue.id));
                return (
                  <label key={venue.id} className={`venue-list-item ${checked ? 'selected' : ''}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleVenueSelection(venue)} />
                    <span className="venue-name">{venue.name}</span>
                    <span className="venue-pref">{venue.prefecture}</span>
                  </label>
                );
              })}
          </div>
        </DsModal>

        {/* ローディング */}
        {isLoadingDisplay && (
          renderEventListSkeleton()
        )}

        {/* ビュー切替：カード / リスト / カレンダー */}
        {!isLoadingDisplay && (viewMode === 'calendar' ? (
          <div className={`calendar-card calendar-card--list-page${calendarLoading ? ' calendar-card--loading' : ''}`}>
            <div className="calendar-card__header calendar-card__header--compact calendar-card__header--with-scope">
              <div className="calendar-month-nav">
                <DsButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon
                  className="calendar-nav-action"
                  onClick={handleCalendarPrevMonth}
                  aria-label="前の月へ"
                >
                  ◀
                </DsButton>
                <div className="calendar-month-label">
                  {formatYearMonthJP(calendarMonth)}
                </div>
                <DsButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon
                  className="calendar-nav-action"
                  onClick={handleCalendarNextMonth}
                  aria-label="次の月へ"
                >
                  ▶
                </DsButton>
              </div>
              <div className="segmented calendar-scope-toggle" role="group" aria-label="表示範囲">
                <button
                  type="button"
                  className={`segmented__item${filterAssignedOnly ? ' is-active' : ''}`}
                  onClick={() => handleCalendarScopeChange(true)}
                  aria-pressed={filterAssignedOnly}
                >
                  担当
                </button>
                <button
                  type="button"
                  className={`segmented__item${!filterAssignedOnly ? ' is-active' : ''}`}
                  onClick={() => handleCalendarScopeChange(false)}
                  aria-pressed={!filterAssignedOnly}
                >
                  全体
                </button>
              </div>
            </div>
            {calendarLoading && (
              <div className="calendar-progress" aria-hidden="true">
                <span />
              </div>
            )}
            {calendarError && (
              <div className="calendar-events-panel__message calendar-events-panel__message--error calendar-inline-error">
                {calendarError}
                <DsButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="calendar-retry-action"
                  onClick={() => fetchCalendarMonthEvents(calendarMonth)}
                >
                  再取得
                </DsButton>
              </div>
            )}
            <div className="calendar-shell">
              <div className="calendar-weekdays">
                {weekdayLabels.map((label, index) => {
                  const classes = ['calendar-weekday'];
                  if (index === 0) classes.push('calendar-weekday--sun');
                  if (index === 6) classes.push('calendar-weekday--sat');
                  return (
                    <div key={label} className={classes.join(' ')}>
                      {label}
                    </div>
                  );
                })}
              </div>
              <div className="calendar-grid">
                {calendarWeeks.map((week, weekIndex) => (
                  <div key={weekIndex} className="calendar-row">
                    {week.map((day) => {
                      const dateKey = normalizeDateKey(day);
                      const dayEvents = dateKey ? calendarEventsByDate[dateKey] || [] : [];
                      const dayNumber = String(day.getDate());
                      const weekday = day.getDay();
                      const cellClasses = ['calendar-cell'];
                      if (!isSameMonth(day, calendarMonth)) {
                        cellClasses.push('calendar-cell--other-month');
                      }
                      if (weekday === 0) cellClasses.push('calendar-cell--sun');
                      if (weekday === 6) cellClasses.push('calendar-cell--sat');
                      if (calendarSelectedDate && isSameDay(day, calendarSelectedDate)) {
                        cellClasses.push('calendar-cell--selected');
                      }
                      if (isSameMonth(day, calendarMonth) && isSameDay(day, today)) {
                        cellClasses.push('calendar-cell--today');
                      }

                      const visibleEvents = dayEvents.slice(0, MAX_VISIBLE_EVENTS_PER_DAY);
                      const remainingCount = dayEvents.length - visibleEvents.length;

                      return (
                        <button
                          key={dateKey || day.toISOString()}
                          type="button"
                          className={cellClasses.join(' ')}
                          onClick={() => handleCalendarDateClick(day)}
                        >
                          <div className="calendar-cell__date">{dayNumber}</div>
                          <div className="calendar-cell__events">
                            {calendarLoading && visibleEvents.length === 0 ? (
                              <>
                                <SkeletonLine className="calendar-event-skeleton" />
                                <SkeletonLine className="calendar-event-skeleton calendar-event-skeleton--short" />
                              </>
                            ) : visibleEvents.map((ev) => {
                              const fullLabel = getEventDisplayTitle(ev);
                              // 追加日程はダッシュボードのカレンダーと同じく淡色チップで区別する。
                              const chipClass = getCalendarEventChipClass(ev);
                              return (
                                <div
                                  key={getEventOccurrenceKey(ev, `${dateKey}-${fullLabel}`)}
                                  className={chipClass}
                                  data-full-label={fullLabel}
                                >
                                  <span className="event-chip__label">{fullLabel}</span>
                                </div>
                              );
                            })}
                            {remainingCount > 0 && (
                              <div className="event-chip event-chip--more">
                                <span className="event-chip__label">
                                  +{remainingCount}件のイベント
                                </span>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className="calendar-events-panel">
              {!calendarSelectedDate && (
                <div className="calendar-events-panel__message">
                  日付を選択してください
                </div>
              )}
              {calendarSelectedDate && (
                <>
                  <div className="calendar-events-panel__title">
                    {formatYMD(calendarSelectedDate)} のイベント
                  </div>
                  {calendarSelectedEvents.length === 0 ? (
                    <div className="calendar-events-panel__message">
                      この日に登録されているイベントはありません。
                    </div>
                  ) : (
                    <div className="event-list-rows">
                      {calendarSelectedEvents.map(renderEventRow)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : viewMode === 'card' ? (
          <div ref={cardGridRef} className="event-list" style={cardGridStyle}>
            {currentEvents.map((event, index) => {
              const occurrenceKey = getEventOccurrenceKey(event, index);
              const isTodayEvent = isToday(event.event_date);
              const isCancelledEvent = !!event?.canceled_at;
              const imgSrc = resolveThumbSrc(event);
              const isLandscapeFlyer = !!flyerLandscapeMap[occurrenceKey];
              const allGenreObjs = resolveGenreObjsAll(event.genre);
              const isConciergeEvent = Boolean(event?.is_concierge_event || event?.concierge_application_id);
              const rotatorBadges = [
                ...(isConciergeEvent ? [{
                  id: 'concierge',
                  label: 'コンシェルジュ',
                  color: 'var(--purple-600)',
                  text_color: '#fff',
                }] : []),
                ...allGenreObjs,
              ];
              const venueDisplay = resolveVenueDisplay(event);
              const venueForCard =
                venueDisplay.name || venueDisplay.link
                  ? { ...venueDisplay, name: venueDisplay.name || '会場未設定' }
                  : null;
              const taskStats = canViewEventTasks
                ? getTaskProgressStats(taskSummaryMap[event.event_uuid])
                : null;
              const castingStats = canViewCastingProgress
                ? getCastingProgressStats(castingSummaryMap[event.event_uuid])
                : { percent: 0 };
              const ticketStats = canViewTicketSales
                ? getTicketProgressStats(ticketSummaryMap[event.event_uuid])
                : { percent: 0 };
              const progressItems = [
                ...(canViewEventTasks
                  ? [
                      {
                        key: 'task',
                        label: 'Tasks',
                        percent: taskStats?.percent ?? 0,
                        tone: 'task',
                      },
                    ]
                  : []),
                ...(canViewCastingProgress
                  ? [
                      {
                        key: 'casting',
                        label: 'Casting Status',
                        percent: castingStats.percent,
                        tone: 'casting',
                      },
                    ]
                  : []),
                ...(canViewTicketSales
                  ? [
                      {
                        key: 'ticket',
                        label: 'Ticket Sales',
                        percent: ticketStats.percent,
                        tone: 'ticket',
                      },
                    ]
                  : []),
              ];
              const flyerReveal = index <= flyerRevealIndex;
              const flyerDelayMs = Math.min(index * 40, 600);
              const flyerDisplayTimeoutMs = index === flyerRevealIndex + 1 ? 1000 : 0;
              const flyerLoading = index < 12 ? 'eager' : 'lazy';
              const flyerFetchPriority = index < 4 ? 'high' : 'auto';
              const handleFlyerReady = () => markFlyerLoaded(index, occurrenceKey);
              const handleCardFlyerLoad = (img) => {
                handleFlyerLoad(occurrenceKey, img);
                handleFlyerReady();
              };

              return (
                <EventListCard
                  key={occurrenceKey}
                  onAssignmentsSaved={handleAssignmentsSaved}
                  event={event}
                  imgSrc={imgSrc}
                  imgAlt={event.name ? `${event.name} のフライヤー` : 'フライヤー'}
                  isLandscape={isLandscapeFlyer}
                  onFlyerLoad={handleCardFlyerLoad}
                  onFlyerError={handleFlyerReady}
                  onFlyerDisplayTimeout={handleFlyerReady}
                  flyerReveal={flyerReveal}
                  flyerDelayMs={flyerDelayMs}
                  flyerDisplayTimeoutMs={flyerDisplayTimeoutMs}
                  flyerLoading={flyerLoading}
                  flyerFetchPriority={flyerFetchPriority}
                  onClick={() => navigate(`/admin/workspaces/event-operations/events/${event.event_uuid}`)}
                  isToday={isTodayEvent}
                  todayLabel="本日"
                  isCancelled={isCancelledEvent}
                  cancelLabel="中止"
                  venueDisplay={venueForCard}
                  badges={rotatorBadges.length > 0 ? <GenreBadgeRotator all={rotatorBadges} /> : null}
                  infoIcons={renderEventInfoIcons(event, `card-info-${event.event_uuid}`)}
                  progressItems={progressItems}
                  isCompact={isCompactCardLayout}
                  noticeLabel={playguideSuggestionEventIds.has(event.event_uuid) ? '券売連動候補あり' : ''}
                />
              );
            })}
            {shouldShowEmptyState && (
              <div className="empty-hint">この条件に合うイベントが見つかりません。</div>
            )}
          </div>
        ) : (
          <div className="event-list-rows">
            {currentEvents.map(renderEventRow)}
            {shouldShowEmptyState && (
              <div className="empty-hint">この条件に合うイベントが見つかりません。</div>
            )}
          </div>
        ))}

        {/* ページネーション */}
        {!isLoadingDisplay && viewMode !== 'calendar' && (
          <ListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            ready={initialReady}
            isLoading={isLoading}
            label="イベント一覧"
          />
        )}
        <ListFootCount show={isMobileSearchLayout && viewMode !== 'calendar' && initialReady} count={totalCount} />
      </div>
    </div>
  );
}
